import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {ContextMenu} from 'primereact/contextmenu';
import {MenuItem} from 'primereact/menuitem';
import {
    TabPanel,
    TabPanelHeaderTemplateOptions,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';
import {Toolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';

import {StoreState} from '~/frontend/src/store';
import PanelPropsValidationErrorDialog from '~/frontend/src/components/base/dialogs/PanelPropsValidationErrorDialog';
import XhrErrorDialog from '~/frontend/src/components/base/dialogs/XhrErrorDialog';
import ConfirmCloseDialog from '~/frontend/src/components/base/dialogs/ConfirmCloseDialog';
import DeleteSuccessDialog from '~/frontend/src/components/base/dialogs/DeleteSuccessDialog';
import PanelPropsDialog from '~/frontend/src/components/base/dialogs/PanelPropsDialog';
import ErrorBoundary from '~/frontend/src/components/base/ErrorBoundary';
import {
    panelClasses,
    PanelType,
    PanelClass
} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {getPanelIndex} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {PanelManagerState} from '~/frontend/src/reducers/panelManager';
import keyboardUtils from '~/frontend/src/utilities/keyboardUtils';
import {createReactMouseEvent} from '~/frontend/src/utilities/reactHelpers';
import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';

import activateOrOpenPanelByPath, {ActivateOrOpenPanelByPath} from '~/frontend/src/actions/panelManager/activateOrOpenPanelByPath';
import activatePanel, {ActivatePanel} from '~/frontend/src/actions/panelManager/activatePanel';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import movePanel, {MovePanel} from '~/frontend/src/actions/panelManager/movePanel';
import openPanel, {
    OpenPanel,
    PanelPropsWithPanelType
} from '~/frontend/src/actions/panelManager/openPanel';
import savePanel, {SavePanel} from '~/frontend/src/actions/panelManager/savePanel';
import closePopup, {ClosePopup} from '~/frontend/src/actions/panelManager/closePopup';
import saveAndClosePopup, {SaveAndClosePopup} from '~/frontend/src/actions/panelManager/saveAndClosePopup';
import confirmClosePanel, {ConfirmClosePanel} from '~/frontend/src/actions/panelManager/confirmClosePanel';
import setScrollPosition, {SetScrollPosition} from '~/frontend/src/actions/panelManager/setScrollPosition';
import cancelRequests, {CancelRequests} from '~/frontend/src/actions/ajax/cancelRequests';
import setTabBarPosition, {SetTabBarPosition} from '~/frontend/src/actions/user/setTabBarPosition';


const Actions = Object.assign( {}, { activateOrOpenPanelByPath, activatePanel, closePanel, movePanel, openPanel, savePanel, closePopup, saveAndClosePopup, confirmClosePanel, cancelRequests, setScrollPosition, setTabBarPosition } ) as any;

export type DragElement = HTMLElement & {
    tabElement : HTMLElement;
    tabIndex : number;
    originalTabIndex : number;
    offsetX : number;
    offsetY : number;
};

type DefaultProps = {
    hideTabBar? : boolean;
    hideTabContent? : boolean;
    hidePanelHeader? : boolean;
}
const defaultProps : Required<DefaultProps> = {
    hideTabBar     : false,
    hideTabContent : false,
    hidePanelHeader: true
};

type Props = {};

export type ComponentProps = Partial<DefaultProps> & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = PanelManagerState & {
    user : User | false | undefined;
};

type ActionProps = {
    activateOrOpenPanelByPath : ActivateOrOpenPanelByPath;
    activatePanel : ActivatePanel;
    closePanel : ClosePanel;
    movePanel : MovePanel;
    openPanel : OpenPanel;
    savePanel : SavePanel;
    closePopup : ClosePopup;
    saveAndClosePopup : SaveAndClosePopup;
    confirmClosePanel : ConfirmClosePanel;
    cancelRequests : CancelRequests;
    setScrollPosition : SetScrollPosition;
    setTabBarPosition : SetTabBarPosition;
}

type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;


type State = {
    contextMenuTabIndex? : number;
}

type BindHandle = () => void;
type UnbindHandle = () => void;

const globalClickListeners : ( ( event : MouseEvent ) => void )[] = [];

export const useGlobalClickListener = ( listener : ( ( event : MouseEvent ) => void ) ) : BindHandle[] => {
    const bindHandle : BindHandle     = () => {
              globalClickListeners.push( listener );
          },
          unbindHandle : UnbindHandle = () => {
              const i = globalClickListeners.findIndex( l => l === listener );

              if ( i > -1 ) {
                  globalClickListeners.splice( i, 1 );
              }
          };

    return [ bindHandle, unbindHandle ];
};

/**
 * PageTabPanel
 */
class PageTabPanel extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    contextMenuRef : ContextMenu | null = null;
    tabView : TabView | null = null;
    dragElement? : DragElement;
    boundGlobalMouseMoveHandler? : ( event : MouseEvent ) => void;
    boundGlobalMouseUpHandler? : ( event : MouseEvent ) => void;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {};

        // bind event handlers
        this.onWindowPopState = this.onWindowPopState.bind( this );
        this.onDocumentClick = this.onDocumentClick.bind( this );
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind( this );
        this.onTabChange = this.onTabChange.bind( this );
        this.onTabScroll = this.onTabScroll.bind( this );
        this.onDragStart = this.onDragStart.bind( this );
        this.onDragMove = this.onDragMove.bind( this );
        this.onDragEnd = this.onDragEnd.bind( this );
        this.onContextMenuHide = this.onContextMenuHide.bind( this );
        this.onToggleTabBarPosition = this.onToggleTabBarPosition.bind( this );
        this.closePopup = this.closePopup.bind( this );
        this.savePopup = this.savePopup.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        window.removeEventListener( 'popstate', this.onWindowPopState, false );
        window.addEventListener( 'popstate', this.onWindowPopState, false );
        document.removeEventListener( 'click', this.onDocumentClick, true );
        document.addEventListener( 'click', this.onDocumentClick, true );
        document.removeEventListener( 'keydown', this.onDocumentKeyDown, true );
        document.addEventListener( 'keydown', this.onDocumentKeyDown, true );

        if ( this.tabView ) {
            const scrollElement = this.tabView.getElement()?.querySelector( '.p-tabview-nav-content' );

            if ( scrollElement ) {
                scrollElement.scrollLeft = this.props.scrollPosition;
            }
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        window.removeEventListener( 'popstate', this.onWindowPopState, false );
        document.removeEventListener( 'click', this.onDocumentClick, true );
        document.removeEventListener( 'keydown', this.onDocumentKeyDown, true );
    }

    componentDidUpdate() {
        if ( this.tabView ) {
            const scrollElement = this.tabView.getElement()?.querySelector( '.p-tabview-nav-content' );

            if ( scrollElement ) {
                scrollElement.scrollLeft = this.props.scrollPosition;
            }
        }
    }

    onWindowPopState( event : PopStateEvent ) {
        const path = document.location.hash;

        let panelKey = event.state?.panelKey;

        if ( panelKey && typeof panelKey === 'object' ) {
            panelKey = Object.values( panelKey ).reduce(
                ( stringPanelKey, char ) => {
                    if ( typeof char === 'string' ) {
                        stringPanelKey += char;
                    }
                    return stringPanelKey;
                },
                ''
            );
        }

        global.directLinkUrl = document.location.href;
        this.props.activateOrOpenPanelByPath( path, panelKey );
    }

    /**
     * Handle clicks on top dom level
     *
     * @param event
     */
    onDocumentClick( event : MouseEvent ) {
        if ( globalClickListeners.length ) {
            for ( const listener of globalClickListeners ) {
                listener( event );
            }
        }
    }

    /**
     * Handle keystrokes on top dom level
     *
     * @param event
     */
    onDocumentKeyDown( event : KeyboardEvent ) {
        const {
                  activePanelKey
              } = this.props;

        let preventDefault : boolean  = false,
            stopPropagation : boolean = false;

        if ( activePanelKey && event.isTrusted ) {

            // [ESC]
            if ( event.key === 'Escape' ) {
                console.info( 'PageTabPanel - onWindowKeyDown' );
                console.info( '    [ESC]' );

                if ( this.props.cancelRequests( activePanelKey ) ) {
                    preventDefault = true;
                    stopPropagation = true;
                }
            }

            // [CMD]/[STRG] + [s]
            else if ( keyboardUtils.cmdKey( event ) && event.key === 's' ) {
                console.info( 'PageTabPanel - onWindowKeyDown' );
                console.info( '    [CMD]/[STRG] + [s]' );

                preventDefault = true;
                stopPropagation = true;

                this.props.savePanel( activePanelKey );
            }
        }

        if ( preventDefault ) {
            console.info( '    -> preventDefault' );
            event.preventDefault();
        }
        if ( stopPropagation ) {
            console.info( '    -> stopPropagation' );
            event.stopPropagation();
        }
    }

    /**
     * Handler for TabView tabChange event
     *
     * @private
     */
    onTabChange( event : TabViewTabChangeEvent ) {
        // close tab
        if (
            ( event.originalEvent?.target as HTMLElement ).classList.contains( 'yn-tab-close-button' )
        ) {
            this.closePanel( event.index );
        }
        // activate tab
        else {
            this.activatePanel( event.index );
        }
    }

    onTabScroll() {
        if ( this.tabView ) {
            const scrollElement = this.tabView.getElement()?.querySelector( '.p-tabview-nav-content' ) as HTMLElement,
                  nextButton    = this.tabView.getElement()?.querySelector( '.p-tabview-nav-next' ) as HTMLElement;

            if ( scrollElement ) {
                this.props.setScrollPosition( scrollElement.scrollLeft );

                if ( nextButton ) {
                    if ( scrollElement.scrollLeft >= scrollElement.scrollWidth - scrollElement.offsetWidth ) {
                        nextButton.style.visibility = 'hidden';
                    }
                    else {
                        nextButton.style.visibility = '';
                    }
                }
            }
        }
    }

    /**
     * Handler for tab drag start.
     *
     * @param event
     *
     * @private
     */
    onDragStart( event : React.MouseEvent ) {
        const nativeEvent = event.nativeEvent;

        let tabElement : HTMLElement | undefined = event.target as HTMLElement;

        while ( tabElement.tagName !== 'A' && tabElement.parentNode ) {
            tabElement = ( tabElement.parentNode ) as HTMLElement;
        }

        if ( tabElement.tagName !== 'A' ) {
            tabElement = undefined;
        }

        if ( tabElement ) {
            const tabIndex      = parseInt( tabElement.id.substring( tabElement.id.lastIndexOf( '_' ) + 1 ) ),
                  tabElementBox = tabElement.getBoundingClientRect();

            // create drag clone
            this.dragElement = document.createElement( 'div' ) as unknown as DragElement;
            this.dragElement.appendChild( tabElement.cloneNode( true ) );
            this.dragElement.className = tabElement.parentElement?.className ?? '';
            this.dragElement.id = `tab_${tabIndex}_clone`;
            this.dragElement.tabElement = tabElement.parentElement!;
            this.dragElement.tabIndex = tabIndex;
            this.dragElement.originalTabIndex = tabIndex;
            this.dragElement.offsetX = nativeEvent.pageX - tabElementBox.x;
            this.dragElement.offsetY = nativeEvent.pageY - tabElementBox.y;

            this.dragElement.classList.add( 'p-component' );
            this.dragElement.classList.add( 'yn-page-tabview-li' );
            this.dragElement.classList.add( 'yn-drag-item' );

            this.dragElement.style.width = `${tabElement.parentElement?.offsetWidth}px`;
            this.dragElement.style.left = `calc( ${nativeEvent.pageX - this.dragElement.offsetX}px - .5rem)`;
            this.dragElement.style.top = ( nativeEvent.pageY - this.dragElement.offsetY ) + 'px';

            document.body.appendChild( this.dragElement );

            // create global mouse move handler
            this.boundGlobalMouseMoveHandler = ( e : MouseEvent ) => {
                this.onDragMove( createReactMouseEvent( e, this.dragElement! ) );
            }
            document.querySelector( 'html' )?.addEventListener( 'mousemove', this.boundGlobalMouseMoveHandler );

            // create global mouseup handler
            this.boundGlobalMouseUpHandler = ( e : MouseEvent ) => {
                this.onDragEnd( createReactMouseEvent( e, this.dragElement! ) );
            }
            document.querySelector( 'html' )?.addEventListener( 'mouseup', this.boundGlobalMouseUpHandler );

            // set original to invisible
            this.dragElement.tabElement.classList.add( 'yn-drag-placeholder' );

            nativeEvent.preventDefault();
        }
    }

    /**
     * Handler for tab drag move.
     *
     * @param event
     *
     * @private
     */
    onDragMove( event : React.MouseEvent ) {
        if ( this.dragElement ) {
            const nativeEvent      = event.nativeEvent,
                  parentElement    = this.dragElement.tabElement.parentNode as HTMLElement,
                  parentElementBox = parentElement?.getBoundingClientRect(),
                  tabElements      = parentElement?.querySelectorAll( 'li:not(.p-tabview-ink-bar)' ),
                  oldX             = parseInt( this.dragElement.style.left ),
                  x                = Math.round( nativeEvent.pageX - this.dragElement.offsetX );

            // set position
            this.dragElement.style.left = x + 'px';

            // position within parent
            if ( parentElementBox.x < nativeEvent.pageX && nativeEvent.pageX < parentElementBox.x + parentElementBox.width ) {

                // moved left?
                if ( x < oldX ) {

                    // has to change position with a tab left?
                    for ( let tabIndex = 0; tabIndex < this.dragElement.tabIndex; tabIndex++ ) {
                        const tabElementBox = tabElements[ tabIndex ].getBoundingClientRect();

                        if ( x < tabElementBox.x + ( tabElementBox.width / 2 ) ) {

                            parentElement.removeChild( this.dragElement.tabElement );

                            parentElement.insertBefore( this.dragElement.tabElement, tabElements[ tabIndex ] );

                            this.dragElement.tabIndex = tabIndex;

                            break;
                        }
                    }
                }

                // move right?
                else if ( x > oldX ) {

                    // has to change position with a tab right?
                    for ( let tabIndex = this.dragElement.tabIndex + 1; tabIndex < tabElements.length; tabIndex++ ) {
                        const tabElementBox = tabElements[ tabIndex ].getBoundingClientRect();

                        if ( x > tabElementBox.x - ( tabElementBox.width / 2 ) ) {

                            parentElement.removeChild( this.dragElement.tabElement );

                            if ( tabIndex < tabElements.length - 1 ) {
                                parentElement.insertBefore( this.dragElement.tabElement, tabElements[ tabIndex ].nextSibling );
                            }
                            else {
                                parentElement.appendChild( this.dragElement.tabElement );
                            }

                            this.dragElement.tabIndex = tabIndex;

                            break;
                        }
                    }
                }
            }

            nativeEvent.preventDefault();
        }
    }

    /**
     * Handler for tab drag end.
     *
     * @param event
     *
     * @private
     */
    onDragEnd( event : React.MouseEvent ) {
        const nativeEvent      = event.nativeEvent,
              parentElement    = this.dragElement?.tabElement.parentNode as HTMLElement,
              parentElementBox = parentElement.getBoundingClientRect(),
              tabElements      = parentElement.querySelectorAll( 'li:not(.p-tabview-ink-bar)' );

        // cleanup global mouse move handler
        if ( this.boundGlobalMouseMoveHandler ) {
            document.querySelector( 'html' )?.removeEventListener( 'mousemove', this.boundGlobalMouseMoveHandler );
            delete this.boundGlobalMouseMoveHandler;
        }

        // cleanup global mouseup handler
        if ( this.boundGlobalMouseUpHandler ) {
            document.querySelector( 'html' )?.removeEventListener( 'mouseup', this.boundGlobalMouseUpHandler );
            delete this.boundGlobalMouseUpHandler;
        }

        // place at original position if dropped outside parent and not there
        if (
            (
                nativeEvent.pageX < parentElementBox.x
                || nativeEvent.pageX > parentElementBox.x + parentElementBox.width
                || nativeEvent.pageY < parentElementBox.y
                || nativeEvent.pageY > parentElementBox.y + parentElementBox.height
            )
            && tabElements[ this.dragElement!.originalTabIndex ] !== this.dragElement!.tabElement
        ) {
            parentElement.removeChild( this.dragElement!.tabElement );

            if ( this.dragElement!.originalTabIndex < tabElements.length - 1 ) {
                parentElement.insertBefore( this.dragElement!.tabElement, tabElements[ this.dragElement!.originalTabIndex ] );
            }
            else {
                parentElement.appendChild( this.dragElement!.tabElement );
            }

            this.dragElement!.tabIndex = this.dragElement!.originalTabIndex;
        }

        if ( this.dragElement!.tabIndex !== this.dragElement!.originalTabIndex ) {
            this.props.movePanel( this.dragElement!.originalTabIndex, this.dragElement!.tabIndex );
        }

        // set original to visible
        if ( this.dragElement!.tabElement ) {
            this.dragElement!.tabElement.classList.remove( 'yn-drag-placeholder' );
        }

        // cleanup dragElement
        if ( this.dragElement ) {
            document.body.removeChild( this.dragElement );
            delete this.dragElement;
        }
    }

    onContextMenu( tabIndex : number, event : React.MouseEvent ) {
        this.setState( {
            contextMenuTabIndex: tabIndex
        } );

        this.contextMenuRef?.show( event );

        event.preventDefault();
    }

    onContextMenuHide() {
        this.setState( {
            contextMenuTabIndex: undefined
        } );
    }

    onToggleTabBarPosition() {
        const isVertical = Boolean( user && user.tabBarPosition === 'vertical' );

        this.props.setTabBarPosition( isVertical ? 'horizontal' : 'vertical' );
    }

    activatePanel( panelIndex : number ) {
        const {
                  openPanels
              }           = this.props,
              panelConfig = openPanels[ panelIndex ],
              panelProps  = panelConfig?.panelProps,
              panelKey    = panelProps?.panelKey;

        this.props.activatePanel( panelKey );
    }

    closePanel( panelIndex : number ) {
        const {
                  openPanels
              }            = this.props,
              panelConfig  = openPanels[ panelIndex ],
              panelIsDirty = panelConfig?.isDirty ?? false,
              panelProps   = panelConfig?.panelProps,
              panelKey     = panelProps?.panelKey;

        if ( panelIsDirty /*|| panelIsInvalid*/ ) {
            this.props.confirmClosePanel( panelKey );
        }
        else {
            this.props.closePanel( panelKey );
        }
    }

    closePopup() {
        const {
                  openPopups
              }           = this.props,
              panelConfig = openPopups[ 0 ],
              panelProps  = panelConfig?.panelProps,
              panelKey    = panelProps?.panelKey;

        this.props.closePopup( panelKey );
    }

    savePopup() {
        const {
                  openPopups
              }        = this.props,

              {
                  panelProps,
                  isBlocking,
                  saveOnOk
              }        = openPopups[ 0 ],
              panelKey = panelProps?.panelKey;

        if ( saveOnOk ) {
            this.props.saveAndClosePopup( panelKey );
        }

        if ( !isBlocking ) {
            this.props.closePopup( panelKey );
        }
    }

    /**
     * Builds the contextMenu content for current selected cell.
     */
    getContextMenuModel() : MenuItem[] {
        const {
                  openPanels,
                  closedPanels
              }                   = this.props,
              {
                  contextMenuTabIndex
              }                   = this.state,
              retVal : MenuItem[] = [
                  {
                      label  : __( 'YN.base.pageTabPanel.contextMenu.close' ),
                      command: () => {
                          this.closePanel( contextMenuTabIndex ?? -1 );
                      }
                  },
                  {
                      label  : __( 'YN.base.pageTabPanel.contextMenu.closeOthersAll' ),
                      command: () => {
                          for ( let i = 0; i < openPanels.length; i++ ) {
                              if ( i !== contextMenuTabIndex ) {
                                  this.closePanel( i );
                              }
                          }
                      }
                  },
                  {
                      label  : __( 'YN.base.pageTabPanel.contextMenu.closeAll' ),
                      command: () => {
                          for ( let i = 0; i < openPanels.length; i++ ) {
                              this.closePanel( i );
                          }
                      }
                  },
                  {
                      label  : __( 'YN.base.pageTabPanel.contextMenu.closeOthersLeft' ),
                      command: () => {
                          for ( let i = 0; i < ( contextMenuTabIndex ?? 0 ); i++ ) {
                              this.closePanel( i );
                          }
                      },
                      visible: ( contextMenuTabIndex ?? 0 ) > 0
                  },
                  {
                      label  : __( 'YN.base.pageTabPanel.contextMenu.closeOthersRight' ),
                      command: () => {
                          for ( let i = ( contextMenuTabIndex ?? 0 ) + 1; i < openPanels.length; i++ ) {
                              this.closePanel( i );
                          }
                      },
                      visible: ( contextMenuTabIndex ?? 0 ) < openPanels.length - 1
                  },
                  { separator: true },
                  {
                      label: __( 'YN.base.pageTabPanel.contextMenu.closedPanels' ),
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      items: closedPanels.map( ( closedPanel, index ) => {
                          return {
                              label  : !isInvalidHTML( closedPanel.panelProps.panelTabTitle ) ? parse( closedPanel.panelProps.panelTabTitle ) : closedPanel.panelProps.panelTabTitle,
                              command: ( event ) => {
                                  const panelProps = {
                                      ...closedPanel.panelProps
                                  } as PanelPropsWithPanelType;

                                  this.props.openPanel( panelProps, undefined, event.originalEvent as React.MouseEvent<HTMLElement, MouseEvent>, true )
                              }
                          };
                      } ),
                      style: { width: '25rem' }
                  }
              ];

        return retVal;
    }

    getTabHeader() : React.ReactNode[] {
        const retVal = [] as React.ReactNode[],
              {
                  openPanels,
                  activePanelKey,
                  hidePanelHeader
              }      = this.props;

        for ( let panelIndex = 0; panelIndex < openPanels.length; panelIndex++ ) {
            const {
                      panelProps,
                      isDirty,
                      isInvalid
                  } = openPanels[ panelIndex ],
                  {
                      panelKey,
                      panelTabTitle,
                      panelTabIcon
                  } = panelProps || {};

            if ( !hidePanelHeader && panelKey === activePanelKey ) {
                let leftIcon = `yn-tab-image-icon mr-2 ${panelTabIcon.replace( /yn-tab-image-icon/, '' )}`;

                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                if ( isInvalid || isDirty ) {
                    leftIcon = leftIcon.replace( /(icon-\S*_itemtype_\S*)/, 'yn-tab-image-icon-mask $1-mask' );
                }

                retVal.push(
                    <Toolbar
                        start={[
                            <div
                                key="spacer"
                                style={{ width: '1.5rem' }}
                            />,
                            <i
                                key="separator"
                                className="p-toolbar-separator"
                            />,
                            <Button
                                key="button-previous"
                                icon="pi pi-chevron-left"
                                className="yn-tabpanel-panel-header-button-previous mr-1"
                                text={true}
                                disabled={panelIndex === 0 || openPanels.length < 2}

                                onClick={() => {
                                    this.activatePanel( panelIndex - 1 );
                                }}
                            />,
                            <Button
                                key="button-next"
                                icon="pi pi-chevron-right"
                                className="yn-tabpanel-panel-header-button-next"
                                text={true}
                                disabled={panelIndex === openPanels.length - 1 || openPanels.length < 2}

                                onClick={() => {
                                    this.activatePanel( panelIndex + 1 );
                                }}
                            />
                        ]}
                        center={
                            <div className="yn-tabpanel-panel-header-content">
                                <span className={`yn-tabpanel-panel-header-content-icon ${leftIcon}`}/>
                                <span className="yn-tabpanel-panel-header-content-text">{panelTabTitle}</span>
                            </div>
                        }
                        end={
                            <Button
                                icon="pi pi-times"
                                className="yn-tabpanel-panel-header-button-close"
                                text={true}

                                onClick={() => {
                                    this.closePanel( panelIndex );
                                }}
                            />
                        }
                        className="yn-panel-toolbar yn-tabpanel-panel-header"
                    />
                );
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the tab panels from the openPanels config.
     *
     * @return {React.ReactNode[]}
     *
     * @private
     */
    getTabPanels() : React.ReactNode[] {
        const retVal = [] as React.ReactNode[],
              {
                  hideTabContent,
                  openPanels,
                  activePanelKey
              }      = this.props,
              {
                  contextMenuTabIndex
              }      = this.state;


        for ( let panelIndex = 0; panelIndex < openPanels.length; panelIndex++ ) {
            const
                {
                    panelProps,
                    isDirty,
                    isInvalid,
                    showPanelPropsDialog,
                    showConfirmCloseDialog,
                    showDeleteSuccessDialog
                }                         = openPanels[ panelIndex ],
                {
                    panelType,
                    panelKey,
                    panelTabTitle,
                    panelTabIcon,
                    panelTooltip
                }                         = panelProps || {},
                PanelComponentClass       = panelClasses[ panelType as PanelType ] as unknown as PanelClass,
                PanelComponent            = panelClasses[ panelType as PanelType ] as unknown as React.FC<any>,
                onSaveAndClosePanelAction = PanelComponentClass.onSaveAndClosePanelAction ? PanelComponentClass.onSaveAndClosePanelAction( { ...panelProps, uid: panelKey } ) : undefined,
                // headerTemplate is used to add toolTips
                headerTemplate            = ( options : TabPanelHeaderTemplateOptions ) => {
                    return <>
                        {panelTooltip &&
                            <Tooltip
                                key="chipTooltip"
                                target={`#${options.element.props.id}`}
                                my="left top"
                                at="left bottom"
                                className="yn-tooltip"
                                showDelay={600}
                                disabled={contextMenuTabIndex !== undefined}
                            >
                                {!isInvalidHTML( panelTooltip ) ? parse( panelTooltip ) : panelTooltip}
                            </Tooltip>
                        }
                        <a
                            id={options.element.props.id}
                            role="tab"
                            className="p-tabview-nav-link"
                            onClick={options.onClick}
                            onKeyDown={options.onKeyDown}
                            aria-controls={options.ariaControls}
                            aria-selected={options.selected}

                            draggable={true}
                            onDragStart={this.onDragStart}
                            onDragEnd={this.onDragEnd}

                            onContextMenu={this.onContextMenu.bind( this, options.index )}
                        >
                            {options.element.props.children}
                        </a>
                    </>;
                },
                isVertical                = Boolean( user && user.tabBarPosition === 'vertical' );

            let leftIcon        = `yn-tab-image-icon mr-2 ${panelTabIcon.replace( /yn-tab-image-icon/, '' )}`,
                rightIcon       = 'yn-tab-close-button ml-1',
                headerClassName = '';

            if ( showConfirmCloseDialog ) {
                rightIcon += ' yn-inactive';
            }

            if ( isInvalid ) {
                headerClassName = 'yn-tab-invalid';
            }
            else if ( isDirty ) {
                headerClassName = 'yn-tab-dirty';
            }

            /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
            if (
                ( !isVertical && panelKey !== activePanelKey )
                || ( ( !isVertical || panelKey === activePanelKey ) && isInvalid )
                || ( ( !isVertical || panelKey === activePanelKey ) && isDirty )
            ) {
                leftIcon = leftIcon.replace( /(icon-\S*_itemtype_\S*)/, 'yn-tab-image-icon-mask $1-mask' );
            }
            /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

            retVal.push(
                <TabPanel
                    key={panelKey}
                    header={!isInvalidHTML( panelTabTitle ) ? parse( panelTabTitle ) : panelTabTitle}
                    headerTemplate={headerTemplate}
                    leftIcon={resolveLigatureIcon( leftIcon )}
                    rightIcon={resolveLigatureIcon( rightIcon )}
                    headerClassName={headerClassName}
                    contentClassName="yn-tabpanel"
                >
                    {!hideTabContent &&
                        <>
                            <XhrErrorDialog
                                dataContext={panelKey}
                                maskParentOnly
                            />
                            <ConfirmCloseDialog
                                panelKey={panelKey}
                                onSaveAndClosePanelAction={onSaveAndClosePanelAction}
                                visible={showConfirmCloseDialog}
                            />
                            <DeleteSuccessDialog
                                panelKey={panelKey}
                                visible={showDeleteSuccessDialog}
                            />
                            <PanelPropsDialog
                                panelKey={panelKey}
                                visible={showPanelPropsDialog}
                            />
                            <ErrorBoundary>
                                <PanelComponent
                                    // TODO: discuss how to name it: uid, componentUid or panelKey (tk)
                                    uid={panelKey}
                                    // TODO: discuss difference of uid and dataContext. IMHO multiple components should be able to share a dataContext, therefore data should be loaded by dataContext, not by uid!
                                    dataContext={panelKey}
                                    {...panelProps}
                                />
                            </ErrorBoundary>
                        </>
                    }
                </TabPanel>
            );
        }

        return retVal;
    }

    /**
     * Builds and returns the first popup from the openPopups config.
     */
    getPopup() : React.ReactNode[] {
        const retVal = [] as React.ReactNode[],
              {
                  openPopups
              }      = this.props;

        if ( openPopups?.length ) {
            const
                {
                    panelProps,
                    isInvalid,
                    isBlocking
                }              = openPopups[ 0 ],
                {
                    panelType,
                    panelKey,
                    panelTabTitle
                }              = panelProps || {},
                PanelComponent = panelClasses[ panelType as PanelType ] as unknown as React.FC<any>;

            retVal.push(
                <XhrErrorDialog
                    key={`${panelKey}_errorDialog`}
                    dataContext={panelKey}
                />
            );
            retVal.push(
                <ErrorBoundary
                    key={`${panelKey}_errorBoundary`}
                >
                    <Dialog
                        key={`${panelKey}_popup`}
                        id={`${panelKey}_popup`}

                        visible={true}
                        closable={!isBlocking}
                        onHide={this.closePopup}
                        focusOnShow={false}
                        closeOnEscape={false}
                        className="yn-form-dialog"
                        header={!isInvalidHTML( panelTabTitle ) ? parse( panelTabTitle ) : panelTabTitle}
                        footer={<div>
                            <Button
                                id={`${panelKey}_popup_okButton`}
                                key="okButton"
                                label={__( 'YN.base.common.ok' )}
                                className="p-button-outlined mr-3"
                                disabled={isInvalid}
                                onClick={this.savePopup}
                            />
                            {!isBlocking &&
                                <Button
                                    id={`${panelKey}_popup_cancelButton`}
                                    key="cancelButton"
                                    label={__( 'YN.base.common.cancel' )}
                                    className="p-button-secondary p-button-outlined"
                                    onClick={this.closePopup}
                                />
                            }
                        </div>}
                    >
                        <PanelComponent
                            // TODO: discuss how to name it: uid, componentUid or panelKey (tk)
                            uid={panelKey}
                            // TODO: discuss difference of uid and dataContext. IMHO multiple components should be able to share a dataContext, therefore data should be loaded by dataContext, not by uid!
                            dataContext={panelKey}
                            mode="standalonePopup"
                            showToolbar={false}
                            {...panelProps}
                        />
                    </Dialog>
                </ErrorBoundary>
            );
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  hideTabBar,
                  hideTabContent,
                  hidePanelHeader,
                  activePanelKey,
                  user
              }          = this.props,
              isVertical = Boolean( user && user.tabBarPosition === 'vertical' );

        return <div
            className={`yn-page-tabview-wrapper ${isVertical ? 'yn-page-tabview-vertical' : 'yn-page-tabview-horizontal'}`}
        >
            <PanelPropsValidationErrorDialog/>
            <ContextMenu
                ref={element => this.contextMenuRef = element}

                model={this.getContextMenuModel()}
                onHide={this.onContextMenuHide}
                style={{ width: 'auto' }}
            />
            {this.getPopup()}
            {this.getTabHeader()}
            <TabView
                ref={element => this.tabView = element}
                activeIndex={getPanelIndex( activePanelKey ?? '' ) ?? 0}
                onTabChange={this.onTabChange}
                pt={{
                    navContainer  : {
                        className: hideTabBar || !hidePanelHeader ? 'hidden' : undefined
                    },
                    navContent    : {
                        onScroll: _.debounce( this.onTabScroll, 50 )
                    },
                    panelContainer: {
                        className: hideTabContent ? 'hidden' : undefined
                    }
                }}
                className="yn-page-tabview"
                scrollable={true}
            >
                {this.getTabPanels()}
            </TabView>
            <Tooltip
                key="chipTooltip"
                target=".yn-page-tabview-toggle-button"
                my="left top"
                at="left bottom"
                className="yn-tooltip"
                showDelay={600}
            >
                {__( `YN.base.pageTabPanel.toggleTabBarPosition.${isVertical ? 'vertical' : 'horizontal'}` )}
            </Tooltip>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
                className={`yn-page-tabview-toggle-button material-symbols-outlined${hideTabBar || !hidePanelHeader ? ' hidden' : ''}`}
                onClick={this.onToggleTabBarPosition}
            >
                {isVertical ? 'toolbar' : 'thumbnail_bar'}
            </div>
        </div>;
    }
}


/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    return {
        ...state.panelManager,
        user: state.session.user
    };
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( PageTabPanel );
